import { z } from 'zod';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';
import { DescriptionMaxLength } from './types.dto';

const PermissionRoleSchema = z.literal('MANAGER').or(z.literal('MEMBER'));

export const productAreaNameLimit = 300;

export const ProductAreaPermissionSchema = z.object({
  id: z.number(),
  productAreaId: z.number(),
  userId: z.string().nullish(),
  teamId: z.string().nullish(),
  role: PermissionRoleSchema,
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
});

export const SaveProductAreaPermissionSchema = ProductAreaPermissionSchema.omit({
  id: true,
  productAreaId: true,
  createdAt: true,
  updatedAt: true,
});

export const ProductAreaSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  parentProductAreaId: z.number().nullish(),
  name: z.string().trim().min(1).max(productAreaNameLimit),
  description: z.string().max(DescriptionMaxLength).nullish(),
  productImageUrl: z.string().max(2083).nullish(),
  isGeneral: z.boolean(),
  archivedAt: z.coerce.date().nullish(),
  deletedAt: z.coerce.date().nullish(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
  permissions: z.array(ProductAreaPermissionSchema),
});

export const ProductAreaSummarySchema = ProductAreaSchema.extend({
  childCount: z.number(),
  isManager: z.boolean(),
});

export type ProductAreaDto = z.infer<typeof ProductAreaSummarySchema>;
export type ProductAreaPermissionDto = z.infer<typeof ProductAreaPermissionSchema>;
export type SaveProductAreaPermissionDto = z.infer<typeof SaveProductAreaPermissionSchema>;

export const GetProductAreaResponseSchema = inferApiResultSchema(
  ProductAreaSummarySchema.omit({ permissions: true }),
);
export const GetProductAreaByIdResponseSchema = inferApiResultSchema(ProductAreaSummarySchema);
export const GetProductAreasByIdsResponseSchema = inferApiResultSchema(
  z.array(ProductAreaSummarySchema.omit({ permissions: true })),
);

export type GetProductAreaResponseDto = z.infer<typeof GetProductAreaResponseSchema>;
export type GetProductAreaByIdResponseDto = z.infer<typeof GetProductAreaByIdResponseSchema>;
export type GetProductAreasByIdsResponseDto = z.infer<typeof GetProductAreasByIdsResponseSchema>;

export const CreateProductAreaSchema = ProductAreaSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
  deletedAt: true,
  permissions: true,
  productImageUrl: true,
}).extend({
  productImageKey: z.string().nullish(),
  isGeneral: z.boolean().default(false).optional(),
  permissions: z.array(ProductAreaPermissionSchema).default([]),
});
export type CreateProductAreaDto = z.infer<typeof CreateProductAreaSchema>;

export const CreateProductAreaResponseSchema = inferApiResultSchema(
  ProductAreaSchema.omit({ permissions: true }),
);
export type CreateProductAreaReponseDto = z.infer<typeof CreateProductAreaResponseSchema>;

export const CreateProductAreasSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string().min(1).max(productAreaNameLimit),
  }),
);
export const CreateProductAreasResponseSchema = inferApiResultSchema(CreateProductAreasSchema);
export type CreateProductAreasResponseDto = z.infer<typeof CreateProductAreasResponseSchema>;

export const UpdateProductAreaSchema = ProductAreaSchema.omit({
  id: true,
  accountId: true,
  archivedAt: true,
  deletedAt: true,
  createdAt: true,
  updatedAt: true,
  permissions: true,
  productImageUrl: true,
})
  .extend({
    productImageKey: z.string().nullish(),
    permissions: z.array(SaveProductAreaPermissionSchema),
  })
  .partial();

export type UpdateProductAreaDto = z.infer<typeof UpdateProductAreaSchema>;

export const UpdateProductAreaResponseSchema = inferApiResultSchema(
  ProductAreaSchema.extend({
    permissions: z.array(SaveProductAreaPermissionSchema).nullish(),
  }),
);

export type UpdateProductAreaResponseDto = z.infer<typeof UpdateProductAreaResponseSchema>;

const NoPermProductAreaSchema = ProductAreaSchema.omit({ permissions: true });
export type NoPermProductAreaDto = z.infer<typeof NoPermProductAreaSchema>;

export const GetProductAreasResponseSchema = inferPaginatedApiResponse(
  z.array(NoPermProductAreaSchema),
);
export type GetProductAreasResponseDto = z.infer<typeof GetProductAreasResponseSchema>;

export const ProductAreasWithIdeaCountSchema = ProductAreaSchema.extend({ ideaCount: z.number() });

export const GetProductAreasWithIdeaCountResponseSchema = inferApiResultSchema(
  z.array(ProductAreasWithIdeaCountSchema),
);
export type GetProductAreasWithIdeaCountResponseDto = z.infer<
  typeof GetProductAreasWithIdeaCountResponseSchema
>;

const ProductAreaSummary = ProductAreaSummarySchema.omit({ permissions: true });
export type ProductAreaSummaryDto = z.infer<typeof ProductAreaSummary>;

export const GetProductAreaSummaryResponseSchema = inferPaginatedApiResponse(
  z.array(ProductAreaSummary),
);
export type GetProductAreaSummaryResponseDto = z.infer<typeof GetProductAreaSummaryResponseSchema>;

export const GetProductAreaArchiveCheckResponseSchema = z.array(
  z.object({
    feedbackFormId: z.number(),
  }),
);
export type GetProductAreaArchiveCheckResponseDto = z.infer<
  typeof GetProductAreaArchiveCheckResponseSchema
>;

export const FlattenedProductAreaItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  depth: z.number(),
});
export type FlattenedProductAreaItemDto = z.infer<typeof FlattenedProductAreaItemSchema>;

export const GetFlattenedProductAreasResponseSchema = inferApiResultSchema(
  z.array(FlattenedProductAreaItemSchema),
);
export type GetFlattenedProductAreasResponseDto = z.infer<
  typeof GetFlattenedProductAreasResponseSchema
>;

export const ProductAreaStatsSchema = z.object({
  managers: z.number(),
  members: z.number(),
  feedbackSubmitted: z.number(),
  feedbackProcessed: z.number(),
  feedbackNewSubmissions: z.number(),
  ideasCreated: z.number(),
  ideasPublished: z.number(),
  ideasVotes: z.number(),
  workItemsLinked: z.number(),
  intakeFormsCreated: z.number(),
  intakeFormsPublished: z.number(),
  intakeFormsUnpublished: z.number(),
});
export type ProductAreaStatsDto = z.infer<typeof ProductAreaStatsSchema>;

export const GetProductAreaStatsResponseSchema = inferApiResultSchema(ProductAreaStatsSchema);
export type GetProductAreaStatsResponseDto = z.infer<typeof GetProductAreaStatsResponseSchema>;
